import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Trophy, Swords, Disc, ShieldAlert } from "lucide-react";

export default function PublicRacerModal({ racerId, onClose }) {
  const [racer, setRacer] = useState(null);
  const [syndicate, setSyndicate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [careerStats, setCareerStats] = useState([]);
  const [selectedSeasonIdx, setSelectedSeasonIdx] = useState(0);

  useEffect(() => {
    if (!racerId) return;
    setLoading(true);
    setCareerStats([]);
    setSelectedSeasonIdx(0);
    
    Promise.all([
      api.get(`/racers/${racerId}`),
      api.get(`/stats/racer/${racerId}/seasons`)
    ])
      .then(async ([rRes, sRes]) => {
        setRacer(rRes.data);
        setCareerStats(sRes.data || []);
        
        if (rRes.data.syndicate_id) {
          const sRes = await api.get(`/syndicates/${rRes.data.syndicate_id}`);
          setSyndicate(sRes.data);
        } else {
          setSyndicate(null);
        }
      })
      .catch((err) => {
        console.error("Failed to load public racer profile:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [racerId]);

  if (!racerId) return null;

  const currentStats = careerStats[selectedSeasonIdx];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-void/80 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-md rtrl-card carbon-bg p-8 border border-surface-line shadow-[0_0_50px_rgba(229,25,25,0.15)] relative">
        {/* Scanline & grid aesthetic */}
        <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
        <div className="scanline" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/40 hover:text-rtrl-red p-1.5 transition-colors"
          data-testid="racer-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {loading ? (
          <div className="py-12 text-center space-y-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-rtrl-red mx-auto"></div>
            <div className="small-label text-rtrl-silver">Accessing HoloNet Roster…</div>
          </div>
        ) : racer ? (
          <div className="space-y-6 relative z-10">
            {/* Header */}
            <div>
              <div className="small-label text-rtrl-red mb-1">Racer Profile uplink</div>
              <h2 className="font-display uppercase text-4xl md:text-5xl text-white tracking-wide">
                {racer.display_name}
              </h2>
              {racer.number !== null && (
                <div className="font-mono-tech text-rtrl-red text-xl font-bold mt-0.5">
                  #{racer.number}
                </div>
              )}
            </div>

            {/* Syndicate Affiliation */}
            <div className="p-4 border border-surface-line/80 bg-void/50 hud-chamfer-sm flex items-center gap-4">
              {syndicate ? (
                <>
                  <div className="w-2 h-12 shrink-0" style={{ background: syndicate.color }} />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase font-mono-tech text-rtrl-silver tracking-wider">Active Syndicate</div>
                    <div className="text-white font-bold text-lg font-body">{syndicate.name}</div>
                    <div className="small-label text-white/40">{syndicate.tag}</div>
                  </div>
                </>
              ) : (
                <>
                  <ShieldAlert className="w-8 h-8 text-white/20 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase font-mono-tech text-rtrl-silver tracking-wider">Status</div>
                    <div className="text-white/50 font-bold font-body">Free Agent</div>
                  </div>
                </>
              )}
            </div>

            {/* Pilot Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-surface-line/40 p-3 hud-chamfer-sm bg-void/30">
                <div className="text-[10px] uppercase font-mono-tech text-white/30">Platform</div>
                <div className="text-white font-body mt-0.5 text-sm">{racer.platform || "—"}</div>
              </div>
              <div className="border border-surface-line/40 p-3 hud-chamfer-sm bg-void/30">
                <div className="text-[10px] uppercase font-mono-tech text-white/30">Region</div>
                <div className="text-white font-body mt-0.5 text-sm">{racer.region || "—"}</div>
              </div>
              <div className="border border-surface-line/40 p-3 hud-chamfer-sm bg-void/30 col-span-2">
                <div className="text-[10px] uppercase font-mono-tech text-white/30 flex items-center gap-1">
                  <Swords className="w-3 h-3 text-rtrl-red" /> Discipline Class
                </div>
                <div className="text-white font-body mt-0.5 text-sm uppercase tracking-wide">
                  {racer.discipline === "both" ? "Dual (Pod & Speeder)" : `${racer.discipline} racing`}
                </div>
              </div>
            </div>

            {/* Career Season Statistics Breakdown */}
            {careerStats.length > 0 ? (
              <div className="space-y-4 pt-2 border-t border-surface-line/40">
                <div className="flex items-center justify-between">
                  <span className="small-label text-rtrl-silver font-semibold">Championship Stats:</span>
                  <select
                    className="bg-void border border-surface-line px-2 py-1 text-xs text-white outline-none rounded font-mono-tech"
                    value={selectedSeasonIdx}
                    onChange={(e) => setSelectedSeasonIdx(parseInt(e.target.value, 10))}
                    data-testid="racer-modal-season-select"
                  >
                    {careerStats.map((cs, idx) => (
                      <option key={cs.season.id} value={idx}>{cs.season.name}</option>
                    ))}
                  </select>
                </div>

                {currentStats && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div className="bg-void/40 p-2 border border-surface-line/20 rounded">
                        <div className="text-[10px] text-white/40 uppercase font-mono-tech">PTS</div>
                        <div className="text-white font-display text-2xl font-bold mt-0.5">{currentStats.points}</div>
                      </div>
                      <div className="bg-void/40 p-2 border border-surface-line/20 rounded">
                        <div className="text-[10px] text-white/40 uppercase font-mono-tech">Wins</div>
                        <div className="text-emerald-400 font-display text-2xl font-bold mt-0.5">{currentStats.wins}</div>
                      </div>
                      <div className="bg-void/40 p-2 border border-surface-line/20 rounded">
                        <div className="text-[10px] text-white/40 uppercase font-mono-tech">Podiums</div>
                        <div className="text-amber-400 font-display text-2xl font-bold mt-0.5">{currentStats.podiums}</div>
                      </div>
                      <div className="bg-void/40 p-2 border border-surface-line/20 rounded">
                        <div className="text-[10px] text-white/40 uppercase font-mono-tech">DNFs</div>
                        <div className="text-rtrl-red font-display text-2xl font-bold mt-0.5">{currentStats.dnfs}</div>
                      </div>
                    </div>

                    {/* Historical Race Placements */}
                    <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
                      <div className="text-[10px] text-white/40 uppercase font-mono-tech pb-1 border-b border-surface-line/10">Race Records ({currentStats.races} races)</div>
                      {currentStats.results.map((r, rIdx) => (
                        <div key={rIdx} className="flex justify-between items-center bg-void/10 border border-surface-line/10 px-2.5 py-1.5 rounded text-xs">
                          <span className="text-white/60 truncate font-mono-tech max-w-[180px]">R{r.round_number} M{r.race_number} · {r.track_name}</span>
                          <div className="flex items-center gap-2 font-bold font-mono-tech shrink-0">
                            <span className={r.dnf ? "text-rtrl-red" : r.position === 1 ? "text-amber-400" : "text-white"}>
                              {r.dnf ? "DNF" : `P${r.position}`}
                            </span>
                            <span className="text-white/30 text-[10px] font-normal">+{r.points}pts</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-white/30 text-xs italic py-2 border-t border-surface-line/10 text-center">No past championship points records on file.</div>
            )}

            {/* External Links / Socials */}
            <div className="space-y-3 pt-2">
              {racer.epic_id && (
                <div className="flex justify-between items-center text-sm border-b border-surface-line/30 pb-2">
                  <span className="text-white/40 text-xs uppercase font-mono-tech">Epic Games ID</span>
                  <span className="text-white font-mono-tech font-bold" data-testid="racer-modal-epic">{racer.epic_id}</span>
                </div>
              )}
              {racer.discord_username && (
                <div className="flex justify-between items-center text-sm border-b border-surface-line/30 pb-2">
                  <span className="text-white/40 text-xs uppercase font-mono-tech flex items-center gap-1">
                    <Disc className="w-3.5 h-3.5 text-indigo-400" /> Discord
                  </span>
                  <span className="text-indigo-400 font-mono-tech font-bold" data-testid="racer-modal-discord">{racer.discord_username}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="py-12 text-center text-white/50">
            Racer profile offline or not found in HoloNet index.
          </div>
        )}
      </div>
    </div>
  );
}